import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import OpenInApp from "../../components/OpenInApp";
import { billingService } from "../../services/billing.service";
import { SubscriptionState } from "../../interfaces/billing.interface";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;

const Success = () => {
  const { t } = useTranslation(["billing", "common"]);
  const [sub, setSub] = useState<SubscriptionState | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const start = Date.now();

    // A fixed-period tick, not a recursive setTimeout — a slow or failed
    // fetch must not push the next attempt out, or a transient 500 mid-poll
    // would silently stretch the 30s budget.
    const tick = async () => {
      // Unconditional: this must run regardless of what the fetch below
      // does, or a persistently failing getSubscription never reaches it
      // and polls forever.
      if (Date.now() - start >= POLL_TIMEOUT_MS) {
        clearInterval(intervalId);
        setTimedOut(true);
        return;
      }
      let next: SubscriptionState;
      try {
        next = await billingService.getSubscription();
      } catch {
        // Retried on the next tick — the payment already succeeded, a
        // failed status check is not grounds to abort the poll.
        return;
      }
      if (cancelled) return;
      setSub(next);
      // Entitlement, not just a settled status: the redirect can beat the
      // webhook, so the API answers `{tier: "free", status: "active"}` with
      // no row written yet. That is still "keep polling", not success.
      if (next.status !== "processing" && next.tier !== "free") {
        clearInterval(intervalId);
      }
    };

    const intervalId = setInterval(() => void tick(), POLL_INTERVAL_MS);
    void tick();

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, []);

  const entitled =
    sub !== null && sub.status !== "processing" && sub.tier !== "free";

  if (timedOut) {
    return (
      <div
        data-testid="page-checkout-success"
        className="card p-8 max-w-md mx-auto mt-12 text-center"
      >
        <h1 className="mb-2 font-display text-2xl font-black">
          {t("checkout.slowTitle")}
        </h1>
        <p className="mb-6 text-ink-soft">{t("checkout.slowBody")}</p>
        <Link to="/account" className="btn">
          {t("common:actions.continue")}
        </Link>
      </div>
    );
  }

  if (entitled) {
    return (
      <div
        data-testid="page-checkout-success"
        className="card p-8 max-w-md mx-auto mt-12 text-center"
      >
        <h1 className="mb-2 font-display text-2xl font-black">
          {t("checkout.successTitle")}
        </h1>
        <p className="mb-6 text-ink-soft">{t("checkout.successBody")}</p>
        {/* The moment a paying subscriber actually needs the app. */}
        <div className="flex flex-wrap items-center justify-center gap-3">
          <OpenInApp />
          <Link to="/account" className="btn-ghost">
            {t("common:actions.continue")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="page-checkout-success"
      aria-live="polite"
      className="card p-8 max-w-md mx-auto mt-12 text-center"
    >
      <h1 className="mb-2 font-display text-2xl font-black">
        {t("checkout.processingTitle")}
      </h1>
      <p className="text-ink-soft">{t("checkout.processingBody")}</p>
    </div>
  );
};

export default Success;
